import { Link, useParams } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { AttentionBadge, AttentionDot } from './ui/attention-badge'
import { SidebarMenuBadge, SidebarTrigger, useSidebar } from './ui/sidebar'
import { useMemberKind } from '../contexts/PermissionsContext'
import { useAttentionCounts } from '../hooks/useAttentionCounts'
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip'
import {
    entryBadge,
    titleWithAttention,
    tooltipParts,
    type AttentionCounts,
    type AttentionEntry,
} from '../lib/attention'
import { useOwnSetting } from '../hooks/useOwnSetting'
import { TIME_ZONE_SETTING, browserTimeZone } from '../lib/digest'

/** The tooltip text of one entry: « 3 nouveaux · 2 en retard · dont 1 critique ». */
export function useAttentionDetails(entry: AttentionEntry, counts: AttentionCounts | undefined): string {
    const { t } = useTranslation()
    return tooltipParts(entry, counts)
        .map((p) => t(`nav.attention.${p.key}`, { count: p.count }))
        .join(' · ')
}

/**
 * NavAttention is the badge of one sidebar entry (spec 2026-10-27-23): a red
 * count linking to the filtered list it counts, a dot on the icon when the
 * sidebar is collapsed, the breakdown in a tooltip. The conversations entry
 * falls back to its neutral count of unresolved senders when nothing is
 * unread.
 */
export function NavAttention({ entry, counts, org }: { entry: AttentionEntry; counts: AttentionCounts | undefined; org: string }) {
    const { t } = useTranslation()
    const { state } = useSidebar()
    const badge = entryBadge(entry, counts)
    const details = useAttentionDetails(entry, counts)
    if (badge.kind === 'none') return null

    if (badge.kind === 'neutral') {
        return (
            <SidebarMenuBadge data-testid="nav-conversations-unresolved" title={details}>
                {badge.count}
            </SidebarMenuBadge>
        )
    }

    if (state === 'collapsed') {
        return (
            <AttentionDot
                className="absolute right-1.5 top-1.5"
                data-testid={`nav-${entry}-attention-dot`}
            />
        )
    }

    const label = t('nav.attention.label', { entry: t(`nav.attention.entry_${entry}`), details })
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span
                    data-sidebar="menu-badge"
                    className="absolute right-1 top-1.5 flex group-data-[collapsible=icon]:hidden"
                >
                    <AttentionLink entry={entry} counts={counts} org={org} label={label}>
                        <AttentionBadge count={badge.count} />
                    </AttentionLink>
                    {/* The neutral count stays reachable when the red one wins. */}
                    {entry === 'conversations' && (counts?.conversations?.unresolved ?? 0) > 0 && (
                        <span className="sr-only" data-testid="nav-conversations-unresolved">
                            {counts?.conversations?.unresolved}
                        </span>
                    )}
                </span>
            </TooltipTrigger>
            <TooltipContent side="right" data-testid={`nav-${entry}-attention-tooltip`}>
                {details}
            </TooltipContent>
        </Tooltip>
    )
}

/** The link of a red badge: the filtered list its count counts. */
function AttentionLink({
    entry,
    counts,
    org,
    label,
    children,
}: {
    entry: AttentionEntry
    counts: AttentionCounts | undefined
    org: string
    label: string
    children: React.ReactNode
}) {
    const common = { 'aria-label': label, 'data-testid': `nav-${entry}-attention`, className: 'rounded-full focus-visible:outline-2' }
    switch (entry) {
        case 'steering':
            return (
                <Link to="/$org/steering" params={{ org }} search={{ attention: counts?.issues?.seen_at } as never} {...common}>
                    {children}
                </Link>
            )
        case 'conversations':
            return (
                <Link
                    to="/$org/conversations"
                    params={{ org }}
                    search={{ status: 'open,waiting_resident', unread: 'true' } as never}
                    {...common}
                >
                    {children}
                </Link>
            )
        case 'documents':
            return (
                <Link to="/$org/documents" params={{ org }} search={{ to_validate: true } as never} {...common}>
                    {children}
                </Link>
            )
        case 'contracts':
            return (
                <Link
                    to="/$org/contracts"
                    params={{ org }}
                    search={{
                        termination_from: counts?.contracts?.window_from,
                        termination_before: counts?.contracts?.window_before,
                    } as never}
                    {...common}
                >
                    {children}
                </Link>
            )
    }
}

/** The collapsed button's accessible name, when its entry carries a red count. */
export function useCollapsedAttentionLabel(entry: AttentionEntry | null, counts: AttentionCounts | undefined): string | undefined {
    const { t } = useTranslation()
    const { state } = useSidebar()
    const details = useAttentionDetails(entry ?? 'steering', counts)
    if (!entry || state !== 'collapsed') return undefined
    const badge = entryBadge(entry, counts)
    if (badge.kind !== 'red') return undefined
    return t('nav.attention.label', { entry: t(`nav.attention.entry_${entry}`), details })
}

/**
 * AttentionTitle prefixes the tab title « (N) » while something waits, so a
 * background tab tells the manager without a pop-up. Whatever sets the title
 * keeps its text: the prefix is re-applied when it changes.
 */
export function AttentionTitle({ total }: { total: number }) {
    useEffect(() => {
        const apply = () => {
            const next = titleWithAttention(document.title, total)
            if (next !== document.title) document.title = next
        }
        apply()
        const titleEl = document.querySelector('title')
        const observer = titleEl ? new MutationObserver(apply) : null
        if (titleEl && observer) observer.observe(titleEl, { childList: true, characterData: true, subtree: true })
        return () => {
            observer?.disconnect()
            document.title = titleWithAttention(document.title, 0)
        }
    }, [total])
    return null
}

/**
 * TimeZoneSync stores the browser's time zone as the member's `time_zone`
 * setting, once per session, when it differs from the stored one: the digest
 * hour is read in it (spec 2026-10-27-23). No country or locale implies a
 * zone; only the browser tells.
 */
export function TimeZoneSync({ org }: { org: string }) {
    const zone = useOwnSetting<string>(org, TIME_ZONE_SETTING)
    const { isLoading, isError, value, save } = zone
    useEffect(() => {
        if (isLoading || isError) return
        const browser = browserTimeZone()
        const key = `time-zone-synced:${org}`
        try {
            if (!browser || browser === value || sessionStorage.getItem(key)) return
            sessionStorage.setItem(key, '1')
        } catch {
            return
        }
        void save(browser).catch(() => undefined)
    }, [isLoading, isError, value, save, org])
    return null
}

/**
 * SidebarTriggerWithAttention is the top bar's sidebar toggle. The app
 * sidebar collapses off-canvas, so once it is closed (or on a phone, where it
 * is a sheet) its entries' badges are out of sight: the toggle then carries
 * the dot, and its accessible name says how many things wait.
 */
export function SidebarTriggerWithAttention({ className }: { className?: string }) {
    const { t } = useTranslation()
    const { state, isMobile } = useSidebar()
    const { org } = useParams({ strict: false }) as { org?: string }
    const kind = useMemberKind()
    const staff = Boolean(org) && kind === 'staff'
    const counts = useAttentionCounts(org ?? '', staff)
    const total = counts?.total ?? 0
    const hidden = isMobile || state === 'collapsed'
    return (
        <span className="relative inline-flex">
            <SidebarTrigger
                className={className}
                aria-label={hidden && total > 0 ? t('nav.attention.trigger', { count: total }) : undefined}
            />
            {hidden && total > 0 && (
                <AttentionDot className="absolute right-0.5 top-0.5" data-testid="nav-attention-dot" />
            )}
        </span>
    )
}
